/**
 * CapabilityPulseTile — compact "capability pulse" for the Mission Control
 * home page. Surfaces the newest coverage movement (a computed one-line pulse,
 * no LLM) plus the cached AI trend narrative when one exists, with a deep-link
 * into Capability Inventory → Analytics. Self-hides for orgs with no trend
 * history so the home page doesn't churn for fresh tenants.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { TrendingUp, TrendingDown, ArrowRight, Activity, Sparkles, Loader2 } from 'lucide-react';
import api from '../../lib/api';
import logger from '../../lib/logger';
import { useAuth } from '../../context/AuthContext';

export default function CapabilityPulseTile() {
  const navigate = useNavigate();
  const { viewAsOrg } = useAuth();
  const [data, setData] = useState(null); // null = loading, {has_data:false} = hide
  const [narrative, setNarrative] = useState(null);
  const [genLoading, setGenLoading] = useState(false);

  useEffect(() => {
    let alive = true;
    const q = viewAsOrg?.id ? `?organization_id=${viewAsOrg.id}` : '';
    api.get(`/capability-inventory/trend-narrative/latest${q}`)
      .then((res) => { if (alive) { setData(res?.data || { has_data: false }); setNarrative(res?.data?.narrative || null); } })
      .catch((e) => { logger.error('capability pulse fetch failed:', e); if (alive) setData({ has_data: false }); });
    return () => { alive = false; };
  }, [viewAsOrg]);

  const generateNarrative = async (e) => {
    e.stopPropagation();
    setGenLoading(true);
    try {
      const { data: res } = await api.post('/capability-inventory/trend-narrative', {
        organization_id: viewAsOrg?.id,
      });
      setNarrative(res?.narrative || null);
    } catch (err) {
      logger.error('capability pulse generate failed:', err);
    } finally {
      setGenLoading(false);
    }
  };

  if (!data || !data.has_data) return null;

  const pulse = data.pulse || {};
  const up = pulse.direction !== 'down';
  const Icon = up ? TrendingUp : TrendingDown;
  const firstSentence = (narrative || '').split(/(?<=\.)\s/)[0];

  const goAnalytics = () => {
    const q = viewAsOrg?.id ? `?view=analytics&organization_id=${viewAsOrg.id}` : '?view=analytics';
    navigate(`/capability-inventory${q}`);
  };

  return (
    <Card
      className="border-cyan-200 bg-gradient-to-br from-white to-cyan-50/50 hover:shadow-md transition-shadow cursor-pointer"
      onClick={goAnalytics}
      data-testid="capability-pulse-tile"
    >
      <CardContent className="p-4 flex items-center gap-4">
        <div className={`shrink-0 h-11 w-11 rounded-xl flex items-center justify-center ${up ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'}`}>
          <Icon className="h-5 w-5" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="text-[10px] font-bold uppercase tracking-wider text-cyan-700">Capability pulse</span>
            <Badge variant="outline" className="text-[9px] py-0 px-1.5 text-cyan-600 border-cyan-300">
              {pulse.months ? `${pulse.months}-month trend` : 'trend'}
            </Badge>
          </div>
          <p className="text-sm font-semibold text-slate-900 truncate mt-0.5" data-testid="capability-pulse-headline">
            {pulse.headline}
          </p>
          {firstSentence ? (
            <p className="text-[11px] text-slate-500 mt-0.5 line-clamp-1 flex items-center gap-1" title={narrative}>
              <Activity className="h-3 w-3 shrink-0" /> {firstSentence}
            </p>
          ) : (
            <button
              onClick={generateNarrative}
              disabled={genLoading}
              className="text-[11px] text-cyan-700 hover:text-cyan-900 font-medium mt-0.5 inline-flex items-center gap-1 disabled:opacity-60"
              data-testid="capability-pulse-generate"
            >
              {genLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />}
              {genLoading ? 'Reading the trend…' : 'Get the AI read'}
            </button>
          )}
        </div>
        <ArrowRight className="h-4 w-4 text-slate-400 shrink-0" />
      </CardContent>
    </Card>
  );
}
